/** Map looker-studio-filter-options API responses to databasetab dropdown values. */

import {
  matchClientFilter,
  matchesAnyClient,
} from './pc-filter-options.util';

export interface ScLookerFilterOptionsResponse {
  clientStatusOptions?: string[];
  clientOptions?: string[];
  segmentOptions?: string[];
  vesselOptions?: string[];
  vesselNamesOptions?: string[];
  vesselStatusOptions?: string[];
  voyageTypeOptions?: string[];
  conditionOptions?: string[];
  typeOfShipOptions?: string[];
  shipTypeOptions?: string[];
  vesselTypeOptions?: string[];
  speedTypeOptions?: string[];
  typeSpeedOptions?: string[];
  technicalOperatorOptions?: string[];
  commercialOperatorOptions?: string[];
}

export function normalizeFilterResponse(res: unknown): ScLookerFilterOptionsResponse {
  const source =
    (res as { data?: ScLookerFilterOptionsResponse })?.data ??
    (res as ScLookerFilterOptionsResponse) ??
    {};
  return {
    clientStatusOptions: source.clientStatusOptions,
    clientOptions: source.clientOptions,
    segmentOptions: source.segmentOptions,
    vesselStatusOptions: source.vesselStatusOptions,
    voyageTypeOptions: source.voyageTypeOptions ?? source.conditionOptions,
    typeOfShipOptions:
      source.typeOfShipOptions ?? source.shipTypeOptions ?? source.vesselTypeOptions,
    speedTypeOptions: source.speedTypeOptions ?? source.typeSpeedOptions,
    technicalOperatorOptions: source.technicalOperatorOptions,
    commercialOperatorOptions: source.commercialOperatorOptions,
    vesselOptions:
      source.vesselOptions ??
      source.vesselNamesOptions ??
      (source as { vesselNames?: string[] }).vesselNames ??
      (source as { shipNameOptions?: string[] }).shipNameOptions,
  };
}

export function withAllOption(options: string[] | null | undefined): string[] {
  const values = (options || []).filter((v) => v != null && String(v).trim() !== '');
  const withoutAll = values.filter((v) => String(v).trim().toUpperCase() !== 'ALL');
  return ['ALL', ...withoutAll];
}

/** Bind API segment options while keeping the ALL sentinel for the dropdown. */
export function bindSegmentOptions(options: string[] | null | undefined): string[] {
  return withAllOption(options);
}

export function extractVesselNameOptions(res: ScLookerFilterOptionsResponse): string[] {
  const raw = res.vesselOptions ?? res.vesselNamesOptions ?? [];
  return raw
    .map((v) => String(v).trim())
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b));
}

export function pickFilterOptions(res: unknown) {
  const normalized = normalizeFilterResponse(res);
  return {
    clientStatusOptions: normalized.clientStatusOptions ?? [],
    clientOptions: normalized.clientOptions ?? [],
    segmentOptions: bindSegmentOptions(normalized.segmentOptions),
    vesselStatusOptions: withAllOption(normalized.vesselStatusOptions),
    voyageTypeOptions: withAllOption(normalized.voyageTypeOptions),
    typeOfShipOptions: withAllOption(normalized.typeOfShipOptions),
    speedTypeOptions: withAllOption(normalized.speedTypeOptions),
    technicalOperatorOptions: withAllOption(normalized.technicalOperatorOptions),
    commercialOperatorOptions: withAllOption(normalized.commercialOperatorOptions),
    vesselNameOptions: extractVesselNameOptions(normalized),
  };
}

export function buildScLookerFilterOptionsPayload(
  username: string | null,
  form: {
    clientStatus?: string | null;
    segment?: string | null;
    vesselStatus?: string | null;
    condition?: string | null;
    typeOfShip?: string | null;
    typeSpeed?: string | null;
    technicalOperator?: string | null;
    commercialOperator?: string | null;
  },
  selectedClients: string[],
  selectedShips: string[],
): Record<string, unknown> {
  return {
    username: username || '',
    client_status: normalizeScFilterValue(form.clientStatus),
    client: selectedClients?.length ? selectedClients : [],
    segment: normalizeScFilterValue(form.segment),
    vessel_status: normalizeScFilterValue(form.vesselStatus),
    voyage_type: normalizeScFilterValue(form.condition),
    type_of_ship: normalizeScFilterValue(form.typeOfShip),
    vessel_names: selectedShips?.length ? selectedShips : [],
    type_speed: normalizeScFilterValue(form.typeSpeed),
    technical_operator: normalizeScFilterValue(form.technicalOperator),
    commercial_operator: normalizeScFilterValue(form.commercialOperator),
  };
}

export function normalizeScFilterValue(val: unknown): string {
  if (val == null || val === '' || val === 'ALL') {
    return '';
  }
  return String(val);
}

/** Whether Client Status has been selected (enables client dropdown — same as databasetab). */
export function isClientStatusFilterEnabled(clientStatus: unknown): boolean {
  if (clientStatus == null || clientStatus === '') {
    return false;
  }
  return String(clientStatus).trim().toUpperCase() !== 'ALL';
}

/** Strip ALL sentinel from API option lists for multi-select filters. */
export function stripAllOption(options: string[] | null | undefined): string[] {
  return (options || [])
    .map((value) => String(value).trim())
    .filter((value) => value && value.toUpperCase() !== 'ALL');
}

/**
 * Applies Active/Inactive client scope from looker-studio-filter-options.
 * When status is set and no specific clients are selected, only allowed API clients match.
 */
export function matchesClientStatusScope(
  itemClient: string | null | undefined,
  selectedClients: string[],
  allowedClients: string[],
  clientStatusEnabled: boolean,
): boolean {
  const client = itemClient || '';

  if (selectedClients.length) {
    return matchesAnyClient(client, selectedClients);
  }

  if (!clientStatusEnabled || !allowedClients.length) {
    return true;
  }

  return allowedClients.some((allowed) => matchClientFilter(client, allowed));
}
